import { Link } from "@inertiajs/react"
import { Check, ChevronDown, ExternalLink } from "lucide-react"
import { useEffect, useRef, type ReactNode, type RefObject } from "react"
import { createPortal } from "react-dom"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { Button } from "@/components/ui/primitives"
import { SIDE_PEEK_ICON_BUTTON_CLASS } from "@/components/ui/side-peek"
import {
  STAT_TILE_CLASS,
  STAT_TILE_INTERACTIVE_CLASS,
  StatTileContent,
  type StatTileContentProps,
} from "@/components/ui/stat-tile"
import { cn } from "@/lib/utils"

const MENU_CLASS = "overflow-hidden rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40"
const ROW_BASE_CLASS =
  "relative grid min-h-9 w-full grid-cols-[120px_minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-2 text-left text-ui-text-primary"
const ROW_CLASS = `${ROW_BASE_CLASS} hover:bg-ui-surface-raised`
const CARD_ROW_CLASS =
  "relative grid w-full grid-cols-[120px_minmax(0,1fr)] items-center gap-3 rounded-lg px-2 py-1.5 text-left text-xs leading-4 text-ui-text-primary"
const CARD_SHELL_CLASS = "flex min-w-0 items-center gap-2 text-left"

const MENU_ITEM_CLASS =
  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-ui-text-primary hover:bg-ui-surface-raised [&>svg]:size-3.5 [&>svg]:shrink-0 [&>svg]:text-ui-text-muted"

/** Меню закрывается по Escape раньше, чем нажатие дойдёт до карточки. */
export function useMenuEscape(open: boolean, onClose: () => void, focusRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      e.preventDefault()
      e.stopPropagation()
      onClose()
      focusRef.current?.focus()
    }
    window.addEventListener("keydown", onKeyDown, true)
    return () => window.removeEventListener("keydown", onKeyDown, true)
  }, [open, onClose, focusRef])
}

/** Список настроек: подпись 120 px, значение в строку, справа маркер. */
export function PeekSettingList({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("-mx-2 flex flex-col gap-0.5", className)}>{children}</div>
}

function SettingLabel({ children }: { children: ReactNode }) {
  return <span className="text-xs leading-4 text-ui-text-muted">{children}</span>
}

/** Значение строки не переносится; ужимается только то, что помечено `truncate`. */
export function PeekSettingValue({ children }: { children: ReactNode }) {
  return (
    <span className="flex min-w-0 items-center gap-2 text-xs leading-4 whitespace-nowrap text-ui-text-primary [&>*]:shrink-0 [&>.truncate]:min-w-0 [&>.truncate]:shrink">
      {children}
    </span>
  )
}

function stopRowOpenEvent(event: { stopPropagation: () => void }) {
  event.stopPropagation()
}

const ROW_OPEN_CLASS = cn(
  SIDE_PEEK_ICON_BUTTON_CLASS,
  "inline-flex size-7 cursor-default items-center justify-center p-0",
)

/**
 * Переход в ячейке маркера: не открывает меню строки.
 * Без `children` — иконка. С `children` — тот же вид, но текст (тикер эпика).
 */
export function PeekRowOpen({
  label,
  href,
  onClick,
  children,
}: {
  label: string
  href?: string
  onClick?: () => void
  children?: ReactNode
}) {
  const content = children ?? <ExternalLink className="size-3.5" />
  const className =
    children != null
      ? cn(
          SIDE_PEEK_ICON_BUTTON_CLASS,
          "inline-flex h-7 cursor-default items-center justify-center px-1.5 font-mono text-xs tabular-nums",
        )
      : ROW_OPEN_CLASS
  // У иконки нет своего текста, у тикера доступное имя — сам текст.
  const ariaLabel = children == null ? label : undefined
  const control =
    href != null ? (
      <Link
        href={href}
        aria-label={ariaLabel}
        draggable={false}
        className={className}
        onClick={stopRowOpenEvent}
        onMouseDown={stopRowOpenEvent}
        onPointerDown={stopRowOpenEvent}
      >
        {content}
      </Link>
    ) : (
      <button
        type="button"
        aria-label={ariaLabel}
        className={className}
        onClick={(event) => {
          stopRowOpenEvent(event)
          onClick?.()
        }}
        onMouseDown={stopRowOpenEvent}
        onPointerDown={stopRowOpenEvent}
      >
        {content}
      </button>
    )

  return control
}

/** Строка только для чтения: подпись и значение. `marker` — необязательная иконка у правого края. */
export function PeekInfoRow({
  label,
  marker,
  layout = "row",
  children,
}: {
  label: string
  marker?: ReactNode
  /** `card` — подпись слева, значение на оставшейся ширине. */
  layout?: "row" | "card"
  children: ReactNode
}) {
  if (layout === "card") {
    return (
      <div className={CARD_ROW_CLASS}>
        <SettingLabel>{label}</SettingLabel>
        <div className={CARD_SHELL_CLASS}>
          <span className="min-w-0 flex-1">{children}</span>
          {marker != null ? marker : null}
        </div>
      </div>
    )
  }
  return (
    <div className={ROW_BASE_CLASS}>
      <SettingLabel>{label}</SettingLabel>
      <PeekSettingValue>{children}</PeekSettingValue>
      {marker != null ? marker : null}
    </div>
  )
}

/** Строка-переход: маркер — иконка внешней ссылки. */
export function PeekLinkRow({
  label,
  href,
  title,
  children,
}: {
  label: string
  href: string
  title?: string
  children: ReactNode
}) {
  return (
    <Link href={href} title={title} className={ROW_CLASS}>
      <SettingLabel>{label}</SettingLabel>
      <PeekSettingValue>{children}</PeekSettingValue>
      <ExternalLink className="size-3.5 text-ui-text-muted" />
    </Link>
  )
}

type PeekMenuRowProps = {
  label: string
  value: ReactNode
  open: boolean
  onToggle: () => void
  onClose: () => void
  menuMinWidth?: number
  menuWidth?: "row" | "content"
  /** Строка неактивна, причина — во всплывающей подсказке. */
  disabledHint?: string
  /** Строка неактивна, без подсказки. */
  disabled?: boolean
  /** Иконка слева от шеврона. Клик по ней не открывает меню. */
  marker?: ReactNode
  /** `card` — подпись слева, значение на оставшейся ширине. Меню то же. */
  layout?: "row" | "card"
  children: ReactNode
}

/** Строка-выбор: маркер — шеврон вниз, меню раскрывается по месту строки. */
export function PeekMenuRow({
  label,
  value,
  open,
  onToggle,
  onClose,
  menuMinWidth = 224,
  menuWidth = "row",
  disabledHint,
  disabled = false,
  marker,
  layout = "row",
  children,
}: PeekMenuRowProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    // Меню раскрывается по месту строки: ширина строки, не уже минимума.
    resolveWidth: (rect) =>
      menuWidth === "content"
        ? { width: "max-content", minWidth: menuMinWidth }
        : { width: rect.width, minWidth: menuMinWidth },
    onOutsideClick: onClose,
  })
  useMenuEscape(open, onClose, buttonRef)

  const rowDisabled = disabled || disabledHint != null
  const menu =
    open && menuStyle && portalRoot
      ? createPortal(
          <div ref={menuRef} role="menu" style={menuStyle} className={MENU_CLASS}>
            {children}
          </div>,
          portalRoot,
        )
      : null
  const chevron = <ChevronDown className="pointer-events-none size-3.5 text-ui-text-muted" />

  if (layout === "card") {
    const cardBody = (
      <>
        <span className="min-w-0 flex-1">{value}</span>
        <span className="flex shrink-0 items-center gap-0.5">
          {marker != null ? (
            <span className="pointer-events-auto relative z-10 flex items-center">{marker}</span>
          ) : null}
          {chevron}
        </span>
      </>
    )
    return (
      <div ref={rootRef} className={cn("relative", rowDisabled && "opacity-50")}>
        <ActionHintTooltip label={disabledHint} className="block w-full">
          <div className={CARD_ROW_CLASS}>
            <button
              ref={buttonRef}
              type="button"
              aria-label={label}
              aria-haspopup="menu"
              aria-expanded={open}
              disabled={rowDisabled}
              onClick={onToggle}
              className={cn(
                "absolute inset-0 rounded-lg hover:bg-ui-surface-raised disabled:pointer-events-none",
                open && "bg-ui-surface-raised",
              )}
            />
            <span className="pointer-events-none">
              <SettingLabel>{label}</SettingLabel>
            </span>
            <div className={cn(CARD_SHELL_CLASS, "pointer-events-none")}>{cardBody}</div>
          </div>
        </ActionHintTooltip>
        {menu}
      </div>
    )
  }

  if (marker == null) {
    return (
      <div ref={rootRef} className="relative">
        <ActionHintTooltip label={disabledHint} className="flex w-full">
          <button
            ref={buttonRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            disabled={rowDisabled}
            onClick={onToggle}
            className={cn(
              ROW_CLASS,
              open && "bg-ui-surface-raised",
              "disabled:pointer-events-none disabled:opacity-50",
            )}
          >
            <SettingLabel>{label}</SettingLabel>
            <PeekSettingValue>{value}</PeekSettingValue>
            {chevron}
          </button>
        </ActionHintTooltip>
        {menu}
      </div>
    )
  }

  return (
    <div ref={rootRef} className={cn("relative", rowDisabled && "opacity-50")}>
      <ActionHintTooltip label={disabledHint} className="block w-full">
        <div className="relative">
          <button
            ref={buttonRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            disabled={rowDisabled}
            onClick={onToggle}
            className={cn(
              "absolute inset-0 rounded-lg hover:bg-ui-surface-raised disabled:pointer-events-none",
              open && "bg-ui-surface-raised",
            )}
          />
          <div className={cn(ROW_BASE_CLASS, "pointer-events-none")}>
            <SettingLabel>{label}</SettingLabel>
            <PeekSettingValue>{value}</PeekSettingValue>
            <span className="flex items-center gap-0.5">
              <span className="pointer-events-auto relative z-10 flex items-center">{marker}</span>
              {chevron}
            </span>
          </div>
        </div>
      </ActionHintTooltip>
      {menu}
    </div>
  )
}

type PeekStatMenuProps = Omit<StatTileContentProps, "trailing"> & {
  open: boolean
  onToggle: () => void
  onClose: () => void
  menuMinWidth?: number
  children: ReactNode
}

/** Плитка-показатель, которая раскрывает меню по месту, как строка-выбор. */
export function PeekStatMenu({ open, onToggle, onClose, menuMinWidth = 260, children, ...tile }: PeekStatMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: (rect) => ({ width: rect.width, minWidth: menuMinWidth }),
    onOutsideClick: onClose,
  })
  useMenuEscape(open, onClose, buttonRef)

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle}
        className={cn(
          STAT_TILE_CLASS,
          STAT_TILE_INTERACTIVE_CLASS,
          "w-full",
          open && "border-ui-border-strong bg-ui-surface-raised",
        )}
      >
        <StatTileContent {...tile} trailing={<ChevronDown className="size-3.5" />} />
      </button>
      {open && menuStyle && portalRoot
        ? createPortal(
            <div ref={menuRef} role="menu" style={menuStyle} className={MENU_CLASS}>
              {children}
            </div>,
            portalRoot,
          )
        : null}
    </div>
  )
}

type PeekAnchoredMenuProps = {
  open: boolean
  onClose: () => void
  menuMinWidth?: number
  trigger: (ref: RefObject<HTMLButtonElement | null>) => ReactNode
  children: ReactNode
}

/** Меню у кнопки в шапке карточки, прижато к правому краю; кнопку рисует вызывающий. */
export function PeekAnchoredMenu({ open, onClose, menuMinWidth = 224, trigger, children }: PeekAnchoredMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    align: "right",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ minWidth: menuMinWidth }),
    onOutsideClick: onClose,
  })
  useMenuEscape(open, onClose, buttonRef)

  return (
    <div ref={rootRef} className="relative inline-flex">
      {trigger(buttonRef)}
      {open && menuStyle && portalRoot
        ? createPortal(
            <div ref={menuRef} role="menu" style={menuStyle} className={MENU_CLASS}>
              {children}
            </div>,
            portalRoot,
          )
        : null}
    </div>
  )
}

type PeekIconMenuProps = {
  label: string
  icon: ReactNode
  open: boolean
  onToggle: () => void
  onClose: () => void
  menuMinWidth?: number
  children: ReactNode
}

/** Для меню из пары коротких пунктов («Изменить», «Удалить»); длинные пункты сами растянут меню. */
export const PEEK_COMPACT_MENU_MIN_WIDTH = 160

/** Кнопка «…» в шапке карточки: меню прижато к правому краю. */
export function PeekIconMenu({ label, icon, open, onToggle, onClose, menuMinWidth, children }: PeekIconMenuProps) {
  return (
    <PeekAnchoredMenu
      open={open}
      onClose={onClose}
      menuMinWidth={menuMinWidth}
      trigger={(ref) => (
        <button
          ref={ref}
          type="button"
          aria-label={label}
          title={label}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={onToggle}
          className={cn(
            "rounded-lg p-1.5 text-ui-text-muted transition-colors hover:bg-ui-surface-raised hover:text-ui-text-primary",
            open && "bg-ui-surface-raised text-ui-text-primary",
          )}
        >
          {icon}
        </button>
      )}
    >
      {children}
    </PeekAnchoredMenu>
  )
}

/** Текстовая кнопка с меню, как «…», но с подписью: «Добавить лимит». */
export function PeekButtonMenu({
  label,
  icon,
  open,
  onToggle,
  onClose,
  menuMinWidth = 260,
  children,
}: PeekIconMenuProps) {
  return (
    <PeekAnchoredMenu
      open={open}
      onClose={onClose}
      menuMinWidth={menuMinWidth}
      trigger={(ref) => (
        <Button
          ref={ref}
          type="button"
          variant="secondary"
          size="sm"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={onToggle}
        >
          {icon}
          {label}
        </Button>
      )}
    >
      {children}
    </PeekAnchoredMenu>
  )
}

export function PeekMenuItem({
  icon,
  label,
  hint,
  danger = false,
  disabled = false,
  onSelect,
}: {
  icon: ReactNode
  label: string
  hint?: string
  danger?: boolean
  disabled?: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        MENU_ITEM_CLASS,
        danger && "text-ui-danger [&>svg]:text-ui-danger",
        hint && "items-start [&>svg]:mt-px",
        "disabled:pointer-events-none disabled:opacity-40",
      )}
    >
      {icon}
      {hint ? (
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="truncate">{label}</span>
          <span className="text-[11px] leading-[15px] text-ui-text-muted">{hint}</span>
        </span>
      ) : (
        <span className="min-w-0 flex-1 truncate">{label}</span>
      )}
    </button>
  )
}

/** Пункт выбора в одну строку: значок слева, галочка у текущего значения. */
export function PeekMenuOption({
  icon,
  label,
  selected,
  onSelect,
  className,
}: {
  icon?: ReactNode
  label: string
  selected: boolean
  onSelect: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(MENU_ITEM_CLASS, className)}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {selected ? <Check /> : null}
    </button>
  )
}

export function PeekMenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-ui-border" />
}

/** Пункт выбора в две строки: радио, заголовок и пояснение под ним. */
export function PeekMenuChoice({
  selected,
  title,
  hint,
  disabled = false,
  onSelect,
}: {
  selected: boolean
  title: string
  hint: string
  disabled?: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-ui-surface-raised disabled:pointer-events-none disabled:opacity-40"
    >
      <span
        className={cn(
          "mt-px inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-ui-border-strong",
          selected && "border-ui-accent bg-ui-accent/20",
        )}
      >
        {selected ? <span className="size-1.5 rounded-full bg-ui-accent" /> : null}
      </span>
      <span className="flex min-w-0 flex-col gap-px">
        <span className="text-xs leading-4 text-ui-text-primary">{title}</span>
        <span className="text-[11px] leading-[15px] text-ui-text-muted">{hint}</span>
      </span>
    </button>
  )
}

/** Три колонки в карточке; на полной странице сетку задаёт className. */
export function PeekTiles({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("grid grid-cols-3 gap-2", className)}>{children}</div>
}

export function PeekTile({ label, value, children }: { label: string; value: string; children?: ReactNode }) {
  return (
    <div className="min-w-0 rounded-lg border border-ui-border bg-ui-surface-sunken px-3 pt-2.5 pb-2">
      <div className="truncate text-[11px] leading-4 text-ui-text-muted">{label}</div>
      <div className="mt-0.5 text-[15px] leading-5 font-semibold tracking-[-0.01em] whitespace-nowrap text-ui-text-primary tabular-nums">
        {value}
      </div>
      {children}
    </div>
  )
}

export function PeekTileSub({ children }: { children: ReactNode }) {
  return <div className="mt-[3px] truncate text-[11px] leading-4 text-ui-text-muted tabular-nums">{children}</div>
}

/** Спарклайн плитки расхода: серия растягивается на всю ширину плитки. */
export function PeekSparkline({ values }: { values: number[] }) {
  const peak = values.length > 1 ? Math.max(...values) : 0
  // Пустое окно рисовать нечем: плоская линия по нулю только шумит.
  if (peak <= 0) return null

  const step = 100 / (values.length - 1)
  const points = values
    .map((value, index) => `${(index * step).toFixed(1)},${(19 - (value / peak) * 18).toFixed(1)}`)
    .join(" ")

  return (
    <svg aria-hidden viewBox="0 0 100 20" preserveAspectRatio="none" className="mt-1 block h-5 w-full">
      <polyline
        fill="none"
        stroke="var(--ui-accent)"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points}
      />
    </svg>
  )
}

export function PeekTileLinks({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("grid grid-cols-2 gap-2", className)}>{children}</div>
}

export function PeekTileLink({
  href,
  title,
  icon,
  children,
}: {
  href: string
  title?: string
  icon: ReactNode
  children: ReactNode
}) {
  return (
    <Link
      href={href}
      title={title}
      className="flex h-10 min-w-0 items-center gap-2.5 rounded-lg border border-ui-border bg-ui-surface-sunken pr-2.5 pl-3 hover:border-ui-border-strong hover:bg-ui-surface-raised"
    >
      <span className="shrink-0 text-ui-text-muted">{icon}</span>
      <span className="min-w-0 truncate text-xs leading-4 text-ui-text-secondary [&>b]:text-[15px] [&>b]:font-semibold [&>b]:tracking-[-0.01em] [&>b]:text-ui-text-primary [&>b]:tabular-nums">
        {children}
      </span>
      <ExternalLink className="ml-auto size-3.5 shrink-0 text-ui-text-muted" />
    </Link>
  )
}
